import { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { api } from 'api/_init'
import { fetchPartnerDetails } from 'api/partner'
import { UPDATE_PARTNER, UPDATE_PERMISSIONS, UPDATE_FEATURES } from 'reducers/user'
import { useToast } from 'components/Shared/AppToast/ToastProvider'

/**
 * Shared partnerId/modal/select-account state for the desktop and mobile
 * account-switcher triggers, so the actual switching behaviour (localStorage
 * + UPDATE_PARTNER + reload) lives in one place.
 */
export function useAccountSwitcher() {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { addNotification } = useToast()
  const partnerId = useSelector((state) => state.user.partnerId)
  // Same signal PartnerSelect.jsx (login-time picker) gates on: cognito JWT
  // partner groups, already in state, no extra fetch.
  const cognitoGroups = useSelector((state) => state.user.cognitoGroups) ?? []
  const hasMultipleAccounts = cognitoGroups.length > 1
  const [showModal, setShowModal] = useState(false)
  const [switchingAccountId, setSwitchingAccountId] = useState(null)

  const selectAccount = useCallback(
    async (accountId) => {
      if (accountId === partnerId) {
        setShowModal(false)
        return
      }

      const previousPartnerId = partnerId
      setSwitchingAccountId(accountId)
      // Must land before the requests below: api/_init.js reads
      // X-Preferred-Partner-Id from the store, so there's no way to "probe"
      // the candidate partner without switching to it first. Undone in catch
      // if anything below fails.
      dispatch({ type: UPDATE_PARTNER, payload: accountId })

      try {
        const [permissionsRes, featuresRes] = await Promise.all([
          api.get('/permissions'),
          api.get('/features'),
        ])
        // Before any dispatch below, so a failure here still only needs the
        // UPDATE_PARTNER rollback. Otherwise only refreshed at login
        // (Cognito/hooks.js), so partnerDetails/userCurrency/attributionModel
        // would keep showing the previous partner's data.
        await fetchPartnerDetails(dispatch, { rethrow: true })
        dispatch({ type: UPDATE_PERMISSIONS, payload: permissionsRes.data ?? [] })
        dispatch({ type: UPDATE_FEATURES, payload: featuresRes.data ?? {} })
        localStorage.setItem('clientId', accountId)
        // Permissions/features are already dispatched and persisted, so a
        // plain reload is safe: PrivateRoute re-checks on remount and
        // redirects on its own if the new partner can't access this route.
        window.location.reload()
      } catch (e) {
        dispatch({ type: UPDATE_PARTNER, payload: previousPartnerId })
        setSwitchingAccountId(null)
        addNotification({ message: t('AccountSwitcher.switchError'), variant: 'error' })
      }
    },
    [dispatch, partnerId, addNotification, t],
  )

  return {
    partnerId,
    hasMultipleAccounts,
    showModal,
    setShowModal,
    switchingAccountId,
    selectAccount,
  }
}
